import { useCallback, useEffect, useState } from 'react';

const LOCAL_STORAGE_KEY = 'user_timezone';
// Default timezone in IANA format (e.g., 'America/New_York', 'Europe/London')
const DEFAULT_TZ = 'Europe/Rome';
const TIMEZONE_CHANGE_EVENT = 'user_timezone_change';

/**
 * Persist user-selected timezone and expose getter/setter.
 * Uses a custom event to synchronize timezone across components.
 */
export function useUserTimezone(): [string, (tz: string) => void] {
    const [timezone, setTimezoneState] = useState<string>(
        () => localStorage.getItem(LOCAL_STORAGE_KEY) ?? DEFAULT_TZ,
    );

    // Listen for timezone changes from other components
    useEffect(() => {
        const handleTimezoneChange = (event: CustomEvent) => {
            const newTimezone = event.detail;

            if (newTimezone !== timezone) {
                setTimezoneState(newTimezone);
            }
        };

        // Add event listener
        window.addEventListener(
            TIMEZONE_CHANGE_EVENT,
            handleTimezoneChange as EventListener,
        );

        // Clean up
        return () => {
            window.removeEventListener(
                TIMEZONE_CHANGE_EVENT,
                handleTimezoneChange as EventListener,
            );
        };
    }, [timezone]);

    // Update localStorage when timezone changes
    useEffect(() => {
        localStorage.setItem(LOCAL_STORAGE_KEY, timezone);
    }, [timezone]);

    // Set timezone and dispatch event to notify other components
    const setTimezone = useCallback(
        (tz: string) => {
            if (tz !== timezone) {
                setTimezoneState(tz);

                // Dispatch custom event to notify other components
                const event = new CustomEvent(TIMEZONE_CHANGE_EVENT, {
                    detail: tz,
                });

                window.dispatchEvent(event);
            }
        },
        [timezone],
    );

    return [timezone, setTimezone];
}
